import {
  MAX_DRAFTS,
  MAX_DRAFT_LENGTH,
  MAX_DRAFT_NAME_LENGTH,
  countCharacters,
  type DraftSummary,
} from '@lyrmo/shared';
import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { useI18n } from '../../i18n/i18n-context';
import { cx } from '../../lib/cx';
import { isToday } from '../../lib/format';
import { canPickFiles, pickDraftFile, suggestName, type DraftFileHandle } from './draft-file';
import { TEXT_FILE_ACCEPT, readTextFile, textFileProblem } from './text-file';
import type { AccountDrafts, SavedDrafts, SavedPlace } from './useSavedDrafts';
import './drafts.css';

// Past this many, the list has a search.
const SEARCH_FROM = 6;

interface DraftsDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** The draft in the editor now. */
  readonly text: string;
  readonly drafts: SavedDrafts;
  /** The writer's account; omitted where there's none to keep drafts in. */
  readonly account?: AccountDrafts | undefined;
  /** Says how a save or an open went, once the dialog has closed for it. */
  readonly onSaved: (place: SavedPlace, how: 'account' | 'computer' | 'download') => void;
  readonly onOpened: (place: SavedPlace) => void;
}

/**
 * Saving the draft, to open later, and opening one saved before: in the writer's account, or as
 * a Markdown file on this computer. The writer chooses where each time.
 */
export function DraftsDialog({ open, onClose, ...props }: DraftsDialogProps) {
  const { t } = useI18n();
  return (
    <Dialog open={open} onClose={onClose} title={t.drafts.title} className="drafts-dialog">
      <Drafts onClose={onClose} {...props} />
    </Dialog>
  );
}

function Drafts({
  onClose,
  text,
  drafts,
  account,
  onSaved,
  onOpened,
}: Omit<DraftsDialogProps, 'open'>) {
  const { t, format, describeError } = useI18n();
  const { place } = drafts;
  const nameId = useId();
  const [name, setName] = useState(() => place?.name ?? suggestName(text));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const signedIn = Boolean(account?.owner);
  const inAccount = place?.kind === 'account' && signedIn;
  const empty = !text.trim();
  const tooLong = countCharacters(text) > MAX_DRAFT_LENGTH;
  const named = name.trim() || t.drafts.untitled;

  /** Does `action`, saying why it failed; the browser's own failures, with files, as `fileFailed`. */
  async function run(action: () => Promise<void>, fileFailed = t.drafts.saveFailed) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof DOMException ? fileFailed : describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  const toAccount = (copy = false) =>
    run(async () => {
      const saved = await drafts.saveToAccount(named, copy);
      onClose();
      onSaved(saved, 'account');
    });

  const toComputer = () =>
    run(async () => {
      const saved = await drafts.saveToComputer(named, t.drafts.untitled);
      if (!saved) return;
      onClose();
      onSaved(saved, saved.kind === 'file' && saved.handle === null ? 'download' : 'computer');
    });

  /** Puts a file's text in the editor, unless it isn't a text file this can read. */
  async function openFile(file: File, handle: DraftFileHandle | null) {
    const problem = textFileProblem(file);
    if (problem) {
      setError(t.editor.feedback[problem]);
      return;
    }
    const read = await readTextFile(file);
    if (read === null) {
      setError(t.editor.feedback.unreadableFile);
      return;
    }
    const opened = drafts.openFromFile(file.name, read, handle);
    onClose();
    onOpened(opened);
  }

  function chooseFile() {
    if (!canPickFiles()) {
      fileRef.current?.click();
      return;
    }
    void run(async () => {
      const picked = await pickDraftFile();
      if (picked) await openFile(picked.file, picked.handle);
    }, t.drafts.openFailed);
  }

  return (
    <div className="drafts">
      <section className="drafts-section" aria-labelledby={`${nameId}-save`}>
        <h3 className="drafts-heading" id={`${nameId}-save`}>
          {t.drafts.saveHeading}
        </h3>
        {empty ? (
          <p className="field-hint">{t.drafts.nothingToSave}</p>
        ) : (
          <form
            className="drafts-save"
            onSubmit={(event) => {
              event.preventDefault();
              if (signedIn && !tooLong) void toAccount();
              else void toComputer();
            }}
          >
            <label className="field-label" htmlFor={nameId}>
              {t.drafts.name}
            </label>
            <input
              id={nameId}
              className="text-input"
              maxLength={MAX_DRAFT_NAME_LENGTH}
              placeholder={t.drafts.untitled}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
            <div className="drafts-actions">
              {account &&
                (signedIn ? (
                  inAccount ? (
                    <>
                      <Button
                        type="submit"
                        variant="primary"
                        icon="cloud"
                        disabled={busy || tooLong}
                      >
                        {busy ? t.drafts.saving : t.drafts.saveChanges}
                      </Button>
                      <Button disabled={busy || tooLong} onClick={() => void toAccount(true)}>
                        {t.drafts.saveCopy}
                      </Button>
                    </>
                  ) : (
                    <Button type="submit" variant="primary" icon="cloud" disabled={busy || tooLong}>
                      {busy ? t.drafts.saving : t.drafts.toAccount}
                    </Button>
                  )
                ) : (
                  <Button
                    icon="cloud"
                    disabled={account.owner === undefined}
                    onClick={account.onSignIn}
                  >
                    {t.drafts.toAccount}
                  </Button>
                ))}
              <Button
                type={signedIn && !tooLong ? 'button' : 'submit'}
                variant={signedIn && !tooLong ? 'ghost' : 'primary'}
                icon="download"
                disabled={busy}
                onClick={
                  signedIn && !tooLong
                    ? () => {
                        void toComputer();
                      }
                    : undefined
                }
              >
                {t.drafts.toComputer}
              </Button>
            </div>
            <p className="field-hint">
              {tooLong && signedIn
                ? t.drafts.tooLong(format.number(MAX_DRAFT_LENGTH))
                : account && !signedIn
                  ? t.drafts.accountPitch
                  : canPickFiles()
                    ? t.drafts.computerNote
                    : t.drafts.downloadNote}
            </p>
          </form>
        )}
      </section>

      <section className="drafts-section" aria-labelledby={`${nameId}-open`}>
        <div className="drafts-heading-row">
          <h3 className="drafts-heading" id={`${nameId}-open`}>
            {t.drafts.openHeading}
          </h3>
          <Button icon="upload" disabled={busy} onClick={chooseFile}>
            {t.drafts.openFile}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept={TEXT_FILE_ACCEPT}
            hidden
            aria-label={t.drafts.fileLabel}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void run(() => openFile(file, null), t.drafts.openFailed);
            }}
          />
        </div>
        {signedIn && account && (
          <AccountList
            account={account}
            current={inAccount ? place.id : null}
            busy={busy}
            run={run}
            onOpen={(id) =>
              run(async () => {
                const opened = await drafts.openFromAccount(id);
                onClose();
                onOpened(opened);
              })
            }
            onDelete={drafts.deleteFromAccount}
          />
        )}
      </section>

      {error && (
        <p className="dialog-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

interface AccountListProps {
  readonly account: AccountDrafts;
  /** The draft in the editor, when it's one of these. */
  readonly current: string | null;
  readonly busy: boolean;
  readonly run: (action: () => Promise<void>) => Promise<void>;
  readonly onOpen: (id: string) => Promise<void>;
  readonly onDelete: (id: string) => Promise<void>;
}

/** The drafts in the writer's account, last saved first, to open or delete. */
function AccountList({ account, current, busy, run, onOpen, onDelete }: AccountListProps) {
  const { t, format, describeError } = useI18n();
  const [list, setList] = useState<DraftSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);
  const listId = useId();
  const { client } = account;

  useEffect(() => {
    let live = true;
    client.listDrafts().then(
      (loaded) => {
        if (live) setList(loaded);
      },
      (caught: unknown) => {
        if (live) setLoadError(describeError(caught));
      },
    );
    return () => {
      live = false;
    };
  }, [client, describeError]);

  if (loadError) {
    return (
      <p className="dialog-error" role="alert">
        {loadError}
      </p>
    );
  }
  if (!list) {
    return (
      <p className="field-hint" role="status">
        {t.drafts.loading}
      </p>
    );
  }

  const query = search.trim().toLocaleLowerCase();
  const shown = query
    ? list.filter((draft) => draft.name.toLocaleLowerCase().includes(query))
    : list;

  return (
    <div className="drafts-account">
      <p className="drafts-subheading" id={listId}>
        {t.drafts.inAccount}
      </p>
      {list.length === 0 ? (
        <p className="field-hint">{t.drafts.empty}</p>
      ) : (
        <>
          {list.length >= SEARCH_FROM && (
            <label className="menu-search drafts-search">
              <Icon name="search" />
              <input
                className="menu-search-input"
                type="search"
                aria-label={t.drafts.find}
                placeholder={t.drafts.find}
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
              />
            </label>
          )}
          {shown.length === 0 ? (
            <p className="field-hint">{t.drafts.noMatch}</p>
          ) : (
            <ul className="draft-list" aria-labelledby={listId}>
              {shown.map((draft) => (
                <li
                  key={draft.id}
                  className={cx('draft-row', draft.id === current && 'is-current')}
                >
                  {deleting === draft.id ? (
                    <div className="draft-confirm" role="group" aria-label={t.drafts.delete}>
                      <p>{t.drafts.confirmDelete(draft.name)}</p>
                      <div className="draft-confirm-actions">
                        <Button
                          onClick={() => {
                            setDeleting(null);
                          }}
                        >
                          {t.common.cancel}
                        </Button>
                        <Button
                          variant="primary"
                          disabled={busy}
                          onClick={() =>
                            void run(async () => {
                              await onDelete(draft.id);
                              setList(
                                (all) => all?.filter((other) => other.id !== draft.id) ?? all,
                              );
                              setDeleting(null);
                            })
                          }
                        >
                          {t.drafts.delete}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="draft-open"
                        aria-label={t.drafts.open(draft.name)}
                        disabled={busy}
                        onClick={() => void onOpen(draft.id)}
                      >
                        <span className="draft-name">{draft.name}</span>
                        <span className="draft-meta">
                          {draft.id === current && (
                            <span className="draft-current">{t.drafts.current}</span>
                          )}
                          <span>
                            {t.drafts.savedAt(
                              format.savedAt(draft.updatedAt),
                              isToday(draft.updatedAt),
                            )}
                          </span>
                          <span>{t.counts.words(draft.words)}</span>
                        </span>
                      </button>
                      <button
                        type="button"
                        className="button button--ghost icon-button"
                        aria-label={t.drafts.deleteDraft(draft.name)}
                        title={t.drafts.deleteDraft(draft.name)}
                        disabled={busy}
                        onClick={() => {
                          setDeleting(draft.id);
                        }}
                      >
                        <Icon name="trash" />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
          {list.length >= MAX_DRAFTS && <p className="field-hint">{t.drafts.limit(MAX_DRAFTS)}</p>}
        </>
      )}
    </div>
  );
}
